import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import deleteButtonIcon from "../../media/delete-button.svg";
import trashButtonIcon from "../../media/trash-button.svg";
import editButtonIcon from "../../media/edit-button.svg";
import httpClient from "../../services/httpClient";
import eventBus from "../eventBus";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faQuestion,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { isBefore } from "date-fns";
import Logger from "../../utils/Logger";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";

// Function to check if reservation has ended
const isReservationEnded = (reservation) => {
  const now = new Date();

  // For car reservations
  if (reservation.endDateLocal) {
    const endDate = new Date(reservation.endDateLocal);
    return isBefore(endDate, now);
  }

  // For room reservations
  if (reservation.endTime) {
    const reservationEndDateTime = new Date(reservation.endTime);
    return isBefore(reservationEndDateTime, now);
  }

  return false;
};

// Function to render invitation response icon
const renderInvitationResponseIcon = (responseStatus) => {
  switch (responseStatus) {
    case 1:
      return (
        <FontAwesomeIcon
          icon={faCheck}
          title="Zaproszenie wstępnie zaakceptowane"
          style={{ color: "#ffc107" }}
        />
      );
    case 2:
      return (
        <FontAwesomeIcon
          icon={faCheck}
          title="Zaproszenie zaakceptowane"
          style={{ color: "#28a745" }}
        />
      );
    case 3:
      return (
        <FontAwesomeIcon
          icon={faXmark}
          title="Zaproszenie odrzucone"
          style={{ color: "#dc3545" }}
        />
      );
    default:
      return (
        <FontAwesomeIcon
          icon={faQuestion}
          title="Brak odpowiedzi na zaproszenie"
          style={{ color: "#6c757d" }}
        />
      );
  }
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatDateTime = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  return (
    date.toLocaleDateString("pl-PL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }) +
    ", " +
    date.toLocaleTimeString("pl-PL", {
      hour: "2-digit",
      minute: "2-digit",
    })
  );
};

// Conversion function for useTranslation support
export function renderCarReservationDetails(
  selectedReservation,
  closeDetailModal,
  userData,
  onSuccess,
  onError,
  navigate,
) {
  return (
    <CarReservationDetails
      selectedReservation={selectedReservation}
      closeDetailModal={closeDetailModal}
      userData={userData}
      onSuccess={onSuccess}
      onError={onError}
      navigate={navigate}
    />
  );
}

function CarReservationDetails({
  selectedReservation,
  closeDetailModal,
  userData,
  onSuccess,
  onError,
  navigate,
}) {
  const { t } = useTranslation();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const deleteCarReservation = async (reservationId) => {
    try {
      await httpClient.delete(`/CarReservations/${reservationId}`, {
        withAuth: true,
      });
      eventBus.emit("car-reservation-deleted", {});
      onSuccess?.();
    } catch (error) {
      onError?.(error);
    } finally {
      setShowDeleteConfirm(false);
      closeDetailModal();
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  return createPortal(
    <>
      <div className="confirmation-overlay" onClick={closeDetailModal}>
        <div className="confirmation-box" onClick={(e) => e.stopPropagation()}>
          <img
            className="close-button"
            src={deleteButtonIcon}
            alt="Close"
            onClick={closeDetailModal}
          />
          <div className="areYouSure">
            <h3
              style={{
                color: "#ff9900",
                fontSize: "1.8rem",
                fontWeight: "500",
                marginBottom: "20px",
              }}
            >
              {t("reservations.details.reservationDetails")}
            </h3>
            <div className="reservation-details">
              <div className="detail-item">
                <strong>{t("reservations.cars.car")}:</strong>{" "}
                {selectedReservation.car && (
                  <span>
                    {selectedReservation.car.make}{" "}
                    {selectedReservation.car.model}
                    {" - "}
                    {selectedReservation.car.licensePlate}
                  </span>
                )}
              </div>
              <div className="detail-item">
                <strong>{t("reservations.cars.departureDateTime")}:</strong>{" "}
                {formatDateTime(selectedReservation.startDateLocal)}
              </div>
              <div className="detail-item">
                <strong>{t("reservations.cars.arrivalDateTime")}:</strong>{" "}
                {formatDateTime(selectedReservation.endDateLocal)}
              </div>
              <div className="detail-item">
                <strong>{t("reservations.details.employee")}:</strong>{" "}
                {`${selectedReservation.user.firstName} ${selectedReservation.user.lastName}` ||
                  t("reservations.details.noData")}
              </div>
              <div className="detail-item">
                <strong>{t("reservations.cars.purpose")}:</strong>{" "}
                {selectedReservation.reason || t("reservations.details.noData")}
              </div>
              <div className="detail-item">
                <strong>{t("reservations.cars.city")}:</strong>{" "}
                {selectedReservation.location ||
                  t("reservations.details.noData")}
              </div>
              {selectedReservation.user?.email === userData.userEmail &&
                !isReservationEnded(selectedReservation) && (
                  <div className="detail-item cursive-center">
                    {t("reservations.details.youAreCreator")}
                    <div className="participant-actions">
                      <button
                        type="button"
                        className="action-button"
                        onClick={() => {
                          closeDetailModal();
                          navigate(
                            `/rezerwacje/samochody/edytuj/${selectedReservation.id}`,
                          );
                        }}
                      >
                        <img
                          src={editButtonIcon}
                          alt={t("reservations.details.editReservation")}
                          style={{ width: 38, height: 38 }}
                        />
                      </button>
                      <button
                        type="button"
                        className="action-button"
                        onClick={handleDeleteClick}
                      >
                        <img
                          src={trashButtonIcon}
                          alt={t("reservations.details.deleteReservation")}
                          style={{ width: 38, height: 38 }}
                        />
                      </button>
                    </div>
                  </div>
                )}
              {userData.userRole === "admin" &&
                selectedReservation.user?.email !== userData.userEmail &&
                !isReservationEnded(selectedReservation) && (
                  <div className="detail-item cursive-center">
                    <div className="participant-actions">
                      <button
                        type="button"
                        className="action-button"
                        onClick={() => {
                          closeDetailModal();
                          navigate(
                            `/rezerwacje/samochody/edytuj/${selectedReservation.id}`,
                          );
                        }}
                      >
                        <img
                          src={editButtonIcon}
                          alt={t("reservations.details.editReservationAdmin")}
                          style={{ width: 38, height: 38 }}
                        />
                      </button>
                      <button
                        type="button"
                        className="action-button"
                        onClick={handleDeleteClick}
                      >
                        <img
                          src={trashButtonIcon}
                          alt={t("reservations.details.deleteReservationAdmin")}
                          style={{ width: 38, height: 38 }}
                        />
                      </button>
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
      <DeleteConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => deleteCarReservation(selectedReservation.id)}
        message={t("reservations.details.confirmDeleteCar")}
      >
        <div className="detail-item">
          <strong>{t("reservations.cars.car")}:</strong>{" "}
          {selectedReservation.car && (
            <span>
              {selectedReservation.car.make} {selectedReservation.car.model}
              {" - "}
              {selectedReservation.car.licensePlate}
            </span>
          )}
        </div>
        <div className="detail-item">
          <strong>{t("reservations.cars.departureDateTime")}:</strong>{" "}
          {formatDateTime(selectedReservation.startDateLocal)}
        </div>
        <div className="detail-item">
          <strong>{t("reservations.cars.arrivalDateTime")}:</strong>{" "}
          {formatDateTime(selectedReservation.endDateLocal)}
        </div>
      </DeleteConfirmationDialog>
    </>,
    document.body,
  );
}

export function renderRecurringReservationDetails(
  selectedReservation,
  closeDetailModal,
  userData,
  onSuccess,
  onError,
  navigate,
) {
  return (
    <RecurringReservationDetails
      selectedReservation={selectedReservation}
      closeDetailModal={closeDetailModal}
      userData={userData}
      onSuccess={onSuccess}
      onError={onError}
      navigate={navigate}
    />
  );
}

function RecurringReservationDetails({
  selectedReservation,
  closeDetailModal,
  userData,
  onSuccess,
  onError,
  navigate,
}) {
  const { t } = useTranslation();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const deleteRecurringReservation = async (reservationId) => {
    try {
      await httpClient.delete(`/RecurringRoomReservations/${reservationId}`, {
        withAuth: true,
      });
      eventBus.emit("room-reservation-deleted", {});
      onSuccess?.();
    } catch (error) {
      Logger.error("Error deleting recurring reservation:", error);
      onError?.(error);
    } finally {
      setShowDeleteConfirm(false);
      closeDetailModal();
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const startDate = new Date(selectedReservation.nearestReservationStartDate);
  const endDate = new Date(selectedReservation.nearestReservationEndDate);

  return createPortal(
    <>
      <div className="confirmation-overlay" onClick={closeDetailModal}>
        <div className="confirmation-box" onClick={(e) => e.stopPropagation()}>
          <img
            className="close-button"
            src={deleteButtonIcon}
            alt="Close"
            onClick={closeDetailModal}
          />
          <div className="areYouSure">
            <h3
              style={{
                color: "#ff9900",
                fontSize: "1.8rem",
                fontWeight: "500",
                marginBottom: "20px",
              }}
            >
              {t("reservations.details.recurringReservationDetails")}
            </h3>
            <div className="reservation-details">
              <div className="detail-item">
                <strong>{t("reservations.rooms.room")}:</strong>{" "}
                {selectedReservation.room || t("reservations.details.noData")}
              </div>
              <div className="detail-item">
                <strong>{t("reservations.details.title")}:</strong>{" "}
                {selectedReservation.title || t("reservations.details.noData")}
              </div>
              {selectedReservation.description && (
                <div className="detail-item">
                  <strong>{t("reservations.rooms.description")}:</strong>{" "}
                  {selectedReservation.description}
                </div>
              )}
              <div className="detail-item">
                <strong>{t("reservations.details.nextReservation")}:</strong>{" "}
                {startDate.toLocaleDateString("pl-PL", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                })}
              </div>
              <div className="detail-item">
                <strong>{t("reservations.details.from")}:</strong>{" "}
                {startDate.toLocaleTimeString("pl-PL", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
                {" - "}
                <strong>{t("reservations.details.to")}:</strong>{" "}
                {endDate.toLocaleTimeString("pl-PL", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </div>
              <div className="detail-item cursive">
                {t("reservations.details.recurringTemplateNote")}
              </div>
              <div className="detail-item cursive-center">
                <div className="participant-actions">
                  <button
                    type="button"
                    className="action-button"
                    onClick={handleDeleteClick}
                  >
                    <img
                      src={trashButtonIcon}
                      alt={t("reservations.details.deleteRecurringReservation")}
                      style={{ width: 38, height: 38 }}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <DeleteConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => deleteRecurringReservation(selectedReservation.id)}
        message={t("reservations.details.confirmDeleteRecurringFull")}
      >
        <div className="detail-item">
          <strong>{t("reservations.rooms.room")}:</strong>{" "}
          {selectedReservation.room || t("reservations.details.noData")}
        </div>
        <div className="detail-item">
          <strong>{t("reservations.details.title")}:</strong>{" "}
          {selectedReservation.title || t("reservations.details.noData")}
        </div>
        {selectedReservation.description && (
          <div className="detail-item">
            <strong>{t("reservations.rooms.description")}:</strong>{" "}
            {selectedReservation.description}
          </div>
        )}
        <div className="detail-item">
          <strong>{t("reservations.details.nextReservation")}:</strong>{" "}
          {startDate.toLocaleDateString("pl-PL", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          })}
        </div>
      </DeleteConfirmationDialog>
    </>,
    document.body,
  );
}

export function renderRoomReservationDetails(
  selectedReservation,
  closeDetailModal,
  userData,
  onSuccess,
  onError,
  navigate,
) {
  return (
    <RoomReservationDetails
      selectedReservation={selectedReservation}
      closeDetailModal={closeDetailModal}
      userData={userData}
      onSuccess={onSuccess}
      onError={onError}
      navigate={navigate}
    />
  );
}

function RoomReservationDetails({
  selectedReservation,
  closeDetailModal,
  userData,
  onSuccess,
  onError,
  navigate,
}) {
  const { t } = useTranslation();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const deleteRoomReservation = async (reservationId, isRecurring = false) => {
    try {
      const endpoint = isRecurring
        ? `/RecurringRoomReservations/${reservationId}`
        : `/RoomReservations/${reservationId}`;

      await httpClient.delete(endpoint, {
        withAuth: true,
      });
      eventBus.emit("room-reservation-deleted", {});
      onSuccess?.();
    } catch (error) {
      Logger.error("Error deleting reservation:", error);
      onError?.(error);
    } finally {
      setShowDeleteConfirm(false);
      closeDetailModal();
    }
  };

  const respondToInvitation = async (id, status) => {
    try {
      await httpClient.put(
        `/RoomReservations/${id}/respond`,
        { status },
        { withAuth: true },
      );
    } catch (error) {
      onError?.(error);
    } finally {
      closeDetailModal();
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const isCreator = selectedReservation.createdBy === userData.userName;
  const userInvitation = selectedReservation.invitations?.find(
    (inv) => inv.userEmail === userData.userEmail,
  );

  return createPortal(
    <>
      <div className="confirmation-overlay" onClick={closeDetailModal}>
        <div className="confirmation-box" onClick={(e) => e.stopPropagation()}>
          <img
            className="close-button"
            src={deleteButtonIcon}
            alt="Close"
            onClick={closeDetailModal}
          />
          <div className="areYouSure">
            <h3
              style={{
                color: "#ff9900",
                fontSize: "1.8rem",
                fontWeight: "500",
                marginBottom: "20px",
              }}
            >
              {t("reservations.details.reservationDetails")}
            </h3>
            <div className="reservation-details">
              <div className="detail-item">
                <strong>{t("reservations.rooms.room")}:</strong>{" "}
                {selectedReservation.room || t("reservations.details.noData")}
              </div>
              <div className="detail-item">
                <strong>{t("reservations.rooms.date")}:</strong>{" "}
                {formatDate(selectedReservation.startTime)}
              </div>
              <div className="detail-item">
                <strong>{t("reservations.rooms.startTime")}:</strong>{" "}
                {new Date(selectedReservation.startTime).toLocaleTimeString(
                  "pl-PL",
                  { hour: "2-digit", minute: "2-digit" },
                )}
              </div>
              <div className="detail-item">
                <strong>{t("reservations.rooms.endTime")}:</strong>{" "}
                {new Date(selectedReservation.endTime).toLocaleTimeString(
                  "pl-PL",
                  { hour: "2-digit", minute: "2-digit" },
                )}
              </div>
              <div className="detail-item">
                <strong>{t("reservations.details.organizer")}:</strong>{" "}
                {selectedReservation.createdBy ||
                  t("reservations.details.noData")}
              </div>
              <div className="detail-item">
                <strong>{t("reservations.rooms.meetingPurpose")}:</strong>{" "}
                {selectedReservation.title || t("reservations.details.noData")}
              </div>
              {selectedReservation.description && (
                <div className="detail-item">
                  <strong>{t("reservations.rooms.description")}:</strong>{" "}
                  {selectedReservation.description}
                </div>
              )}
              <div className="detail-item">
                <strong>{t("reservations.rooms.participants")}:</strong>{" "}
                {selectedReservation.invitations &&
                selectedReservation.invitations.length > 0 ? (
                  <div>
                    <ul>
                      {selectedReservation.invitations.map(
                        (invitation, index) => (
                          <li key={index}>
                            {invitation.userFullName}{" "}
                            {renderInvitationResponseIcon(
                              invitation.status === "NotResponded"
                                ? 0
                                : invitation.status === "InitiallyAccepted"
                                  ? 1
                                  : invitation.status === "Accepted"
                                    ? 2
                                    : 3,
                            )}
                          </li>
                        ),
                      )}
                    </ul>
                  </div>
                ) : (
                  t("reservations.details.noParticipants")
                )}
              </div>
              {selectedReservation.preSignedFileUrl && (
                <div className="detail-item">
                  <strong>{t("reservations.details.attachment")}:</strong>{" "}
                  <a
                    href={selectedReservation.preSignedFileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "#ff9900",
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                  >
                    {t("reservations.details.downloadAttachment")}
                  </a>
                </div>
              )}
              {selectedReservation.isRecurring && (
                <div className="detail-item cursive">
                  {t("reservations.details.recurringReservationNote")}
                </div>
              )}
              {isCreator && !isReservationEnded(selectedReservation) && (
                <div className="detail-item cursive-center">
                  {t("reservations.details.youAreCreator")}
                  <div className="participant-actions">
                    {!selectedReservation.isRecurring && (
                      <button
                        type="button"
                        className="action-button"
                        onClick={() => {
                          closeDetailModal();
                          navigate(
                            `/rezerwacje/sale-konferencyjne/edytuj/${selectedReservation.id}`,
                          );
                        }}
                      >
                        <img
                          src={editButtonIcon}
                          alt={t("reservations.details.editReservation")}
                          style={{ width: 38, height: 38 }}
                        />
                      </button>
                    )}
                    <button
                      type="button"
                      className="action-button"
                      onClick={handleDeleteClick}
                    >
                      <img
                        src={trashButtonIcon}
                        alt={t("reservations.details.deleteReservation")}
                        style={{ width: 38, height: 38 }}
                      />
                    </button>
                  </div>
                </div>
              )}
              {userData.userRole === "admin" &&
                !isCreator &&
                !isReservationEnded(selectedReservation) && (
                  <div className="detail-item cursive-center">
                    <div className="participant-actions">
                      {!selectedReservation.isRecurring && (
                        <button
                          type="button"
                          className="action-button"
                          onClick={() => {
                            closeDetailModal();
                            navigate(
                              `/rezerwacje/sale-konferencyjne/edytuj/${selectedReservation.id}`,
                            );
                          }}
                        >
                          <img
                            src={editButtonIcon}
                            alt={t("reservations.details.editReservationAdmin")}
                            style={{ width: 38, height: 38 }}
                          />
                        </button>
                      )}
                      <button
                        type="button"
                        className="action-button"
                        onClick={handleDeleteClick}
                      >
                        <img
                          src={trashButtonIcon}
                          alt={t("reservations.details.deleteReservationAdmin")}
                          style={{ width: 38, height: 38 }}
                        />
                      </button>
                    </div>
                  </div>
                )}
              {userInvitation && (
                <div className="detail-item cursive-center">
                  {t("reservations.details.youAreParticipant")}
                  <div className="participant-actions">
                    <button
                      type="button"
                      className="action-button"
                      onClick={() =>
                        respondToInvitation(
                          selectedReservation.id,
                          "InitiallyAccepted",
                        )
                      }
                    >
                      {t("reservations.details.initiallyAccept")}
                    </button>
                    <button
                      type="button"
                      className="action-button"
                      onClick={() =>
                        respondToInvitation(selectedReservation.id, "Accepted")
                      }
                    >
                      {t("reservations.details.accept")}
                    </button>
                    <button
                      type="button"
                      className="action-button"
                      onClick={() =>
                        respondToInvitation(selectedReservation.id, "Declined")
                      }
                    >
                      {t("reservations.details.decline")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <DeleteConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() =>
          deleteRoomReservation(
            selectedReservation.id,
            selectedReservation.isRecurring,
          )
        }
        message={
          selectedReservation.isRecurring
            ? t("reservations.details.confirmDeleteRecurring")
            : t("reservations.details.confirmDeleteRoom")
        }
      >
        <div className="detail-item">
          <strong>{t("reservations.rooms.room")}:</strong>{" "}
          {selectedReservation.room || t("reservations.details.noData")}
        </div>
        <div className="detail-item">
          <strong>{t("reservations.rooms.date")}:</strong>{" "}
          {formatDate(selectedReservation.startTime)}
        </div>
        <div className="detail-item">
          <strong>{t("reservations.rooms.startTime")}:</strong>{" "}
          {new Date(selectedReservation.startTime).toLocaleTimeString("pl-PL", {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
      </DeleteConfirmationDialog>
    </>,
    document.body,
  );
}
